// Import głównego pliku stylów
import '../css/style.scss';

// Import AOS
import AOS from 'aos';
import 'aos/dist/aos.css';
import Lenis from 'lenis';
import './homepage-hero-video';

class App {
    constructor() {
        this.lenis = null;
    }

    getSmoothScrollTarget(hash) {
        if (!hash || hash === '#') {
            return null;
        }

        const targetId = decodeURIComponent(hash.slice(1));

        if (!targetId) {
            return null;
        }

        return document.getElementById(targetId) || document.getElementsByName(targetId)[0] || null;
    }

    getSmoothScrollOffset() {
        const headerElement = document.querySelector('.js-headroom');
        const topbarElement = document.getElementById('global-topbar');
        let offset = 0;

        if (topbarElement && !topbarElement.hasAttribute('hidden')) {
            offset = Math.max(offset, topbarElement.getBoundingClientRect().bottom);
        }

        if (headerElement) {
            offset = Math.max(offset, headerElement.getBoundingClientRect().bottom);
        }

        return Math.max(offset, 0) + 12;
    }

    scrollToHash(hash, updateHistory = false) {
        const target = this.getSmoothScrollTarget(hash);

        if (!target) {
            return false;
        }

        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (updateHistory) {
            window.history.pushState(null, '', hash);
        }

        if (this.lenis && !prefersReducedMotion) {
            this.lenis.scrollTo(target, {
                offset: -this.getSmoothScrollOffset(),
                duration: 1.2,
            });

            return true;
        }

        const targetTop = window.scrollY + target.getBoundingClientRect().top - this.getSmoothScrollOffset();

        window.scrollTo({
            top: Math.max(targetTop, 0),
            behavior: prefersReducedMotion ? 'auto' : 'smooth',
        });

        return true;
    }

    initLenis() {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        this.lenis = new Lenis({
            duration: 1.2,
            smoothWheel: true,
            wheelMultiplier: 0.9,
            touchMultiplier: 1.1,
        });

        const raf = (time) => {
            if (!this.lenis) {
                return;
            }

            this.lenis.raf(time);
            window.requestAnimationFrame(raf);
        };

        window.requestAnimationFrame(raf);
    }

    initMobileMenuScrollLock() {
        document.addEventListener('mobilemenu:toggle', (event) => {
            const isActive = Boolean(event.detail?.isActive);
            const scrollbarWidth = Number(event.detail?.scrollbarWidth || 0);

            if (!this.lenis) {
                return;
            }

            if (isActive) {
                this.lenis.stop();
                document.body.style.paddingRight = `${scrollbarWidth}px`;
                return;
            }

            this.lenis.start();
            document.body.style.paddingRight = '0';
        });
    }

    /**
     * Animations On Scroll (AOS)
     */
    initAos() {
        AOS.init({
            duration: 550, // Czas trwania animacji w ms
            easing: 'ease-out-cubic', // Typ easingu
            once: true, // Animacja tylko raz
            offset: 70, // Offset od góry w px
            delay: 0, // Opóźnienie w ms
            anchorPlacement: 'top-bottom', // Punkt zakotwiczenia
            disable: function() {
                // Wyłącz na urządzeniach mobilnych jeśli chcesz
                return window.innerWidth < 768;
            }
        });
        
        console.log('AOS zainicjalizowane');
    }
    
    /**
     * Init headroom.js
     */
    initHeadroom() {
        const headerElement = document.querySelector('.js-headroom');
        if (headerElement && typeof window.Headroom === 'function') {
            var headroom = new Headroom(headerElement);
            headroom.init();
        } else {
            if (!headerElement) {
                console.warn('Element .js-headroom nie został znaleziony');
            }
            if (typeof window.Headroom === 'undefined') {
                console.warn('Headroom library nie jest dostępna');
            }
        }
    }
    
    /**
     * Init viewer.js
     */
    initViewer() {
        const lightBoxes = document.querySelectorAll('.viewer-js');
        if (lightBoxes.length > 0 && typeof window.Viewer === 'function') {
            lightBoxes.forEach(lightBox => {
                const viewer = new Viewer(lightBox, {
                    title: false,
                });
            });
        } else {
            if (lightBoxes.length === 0) {
                console.log('Brak elementów .viewer-js na stronie');
            }
            if (typeof window.Viewer === 'undefined') {
                console.warn('Viewer library nie jest dostępna');
            }
        }
    }
    
    /**
     * Class toggler
     */
    activeClassToggler() {
        const togglers = document.querySelectorAll('.-js-toggler');
        if (togglers) {
            togglers.forEach(toggler => {
                toggler.addEventListener('click', () => {
                    toggler.classList.toggle('active');
                });
            });
        }
    }

    initSmoothScroll() {
        document.addEventListener('click', (event) => {
            if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                return;
            }

            const anchor = event.target.closest('a[href]');

            if (!anchor) {
                return;
            }

            const url = new URL(anchor.href, window.location.href);

            if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || !url.hash) {
                return;
            }

            if (!this.getSmoothScrollTarget(url.hash)) {
                return;
            }

            event.preventDefault();
            this.scrollToHash(url.hash, true);
        });

        window.addEventListener('load', () => {
            if (!window.location.hash) {
                return;
            }

            this.scrollToHash(window.location.hash);
        }, { once: true });
    }
    
    /**
     * Execute on page ready
     */
    pageReady() {
        document.body.classList.add('loaded');
        document.body.classList.remove("preload");
    }
    
    reinit() {
        this.initAos();
    }
    
    init() {
        this.initLenis();
        this.initMobileMenuScrollLock();
        this.initAos();
        this.initHeadroom();
        this.initViewer();
        this.activeClassToggler();
        this.initSmoothScroll();
        this.pageReady();
    }
}

// Inicjalizuj od razu gdy DOM jest gotowy
document.addEventListener('DOMContentLoaded', function() {
    const app = new App();
    app.init();
});

// Automatyczny import wszystkich plików JS (oprócz _app.js)
function requireAll(r) {
    r.keys().forEach(r);
}

// Import wszystkich plików JS z aktualnego folderu (oprócz lib/ i _app.js)
requireAll(require.context('./', false, /^(?!.*\/_app\.js$).*\.js$/));
 